import { useEffect, useLayoutEffect } from "react";

import { CountryNames } from "@simplyk/common";
import useMuiMediaQuery from "@mui/material/useMediaQuery";
import { useRouter } from "next/router";

import { ShowcaseStep, SHOWCASE_CONTENT } from "./showcaseContent";

import { useRegisterExperiment } from "@/features/Auth/Register/helpers/useRegisterExperiment";
import { useUserCountryByCloudflareHeader } from "@/features/Auth/Register/hooks/useUserCountryByCloudflareHeader";
import { RegisterStep } from "@/features/Auth/Register/components/Modals/RegisterModalsContainer";
import { SignUpSessionStorage } from "@/features/Auth/Register/store/SignUpSessionStorage";
import { RegisterCategory } from "@/features/Auth/Register/types";

export const SOLUTION_SHOWCASE_EXPERIMENT_KEY = "register_solution_showcase";

// Experiment paused.
const IS_SOLUTION_SHOWCASE_ENABLED = false;

const DESKTOP_MEDIA_QUERY = "(min-width: 1200px)";

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export const SHOWCASE_STEP_BY_REGISTER_STEP: Partial<Record<RegisterStep, ShowcaseStep>> = {
  "method-choice": "one",
  "user-info": "two",
  eligibility: "three",
  organization: "four",
  qualification: "five",
};

interface UseSolutionShowcaseExperimentResult {
  showShowcase: boolean;
  isResolved: boolean;
}

export const useSolutionShowcaseExperiment = (
  currentStep: RegisterStep | undefined,
  category: RegisterCategory | undefined
): UseSolutionShowcaseExperimentResult => {
  const isDesktopViewport = useMuiMediaQuery(DESKTOP_MEDIA_QUERY, { noSsr: true });
  const router = useRouter();
  const countryOverride =
    typeof window === "undefined"
      ? undefined
      : (new URLSearchParams(window.location.search).get("country") ?? undefined);
  const { countryByCloudflareHeader, isCountryByCloudflareHeaderLoading } = useUserCountryByCloudflareHeader({
    countryOverride,
  });

  const isUsVisitor = countryByCloudflareHeader === CountryNames.UnitedStates;
  const content = category ? SHOWCASE_CONTENT[category] : undefined;
  const showcaseStep = currentStep ? SHOWCASE_STEP_BY_REGISTER_STEP[currentStep] : undefined;
  const isEligible = Boolean(
    IS_SOLUTION_SHOWCASE_ENABLED && isDesktopViewport && isUsVisitor && content && showcaseStep
  );

  const isEnrollmentStep = currentStep === "method-choice";
  const variant = useRegisterExperiment(SOLUTION_SHOWCASE_EXPERIMENT_KEY, {
    enabled: isEligible && isEnrollmentStep,
  });

  const isEnglishVisitor = router.locale?.toLowerCase().startsWith("en") ?? true;
  const isIneligibleRegardlessOfCountry =
    !IS_SOLUTION_SHOWCASE_ENABLED || !isDesktopViewport || !content || !showcaseStep || !isEnglishVisitor;
  const hasStoredBucket =
    typeof window !== "undefined" &&
    Boolean(SignUpSessionStorage.getRegisterExperiment(SOLUTION_SHOWCASE_EXPERIMENT_KEY));
  const isResolved =
    isIneligibleRegardlessOfCountry ||
    variant !== null ||
    (isEnrollmentStep ? !isCountryByCloudflareHeaderLoading && !isUsVisitor : !hasStoredBucket);
  const showShowcase = IS_SOLUTION_SHOWCASE_ENABLED && variant === "test" && Boolean(content && showcaseStep);

  useIsomorphicLayoutEffect(() => {
    if (isResolved && !showShowcase) {
      document.body.classList.remove("register-showcase-hold");
    }
  }, [isResolved, showShowcase]);

  return { showShowcase, isResolved };
};
